import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantsService } from '../../../services/tenants.service';
import { TriggerRule } from '../../../model/trigger-rule';
import { TriggerRulesService } from '../../../services/trigger-rules.service';
import { TriggerRulePersistRequest } from '../../../model/requests/trigger-rule-persist-request';
import { JsonEditorService } from '../../../services/json-editor.service';
import { Tenant } from '../../../model/tenant';
import { Organization } from '../../../model/organization';
import { noSpecialCharacters, noWhitespaces } from '../../../shared/custom-validators';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AuthGuard } from '../../../common/auth-guard.service';
import { LoggedUser } from '../../../common/logged-user.service';
import { CommonMessagesService } from '../../../services/common-messages.service';
import {TranslocoService} from '@ngneat/transloco';

@Component({
  selector: 'app-trigger-rules-form',
  templateUrl: './trigger-rules-form.component.html',
  styleUrls: ['./trigger-rules-form.component.scss']
})
export class TriggerRulesFormComponent implements OnInit, ComponentCanDeactivate {

  triggerRule: TriggerRule;

  types: any[];
  tenants: Tenant[];
  organizations: Organization[];

  triggerRuleForm: FormGroup;
  persistingChanges: any;
  formSuccess: boolean;

  mode: 'create' | 'edit';
  public editInfo: {oldName: string, tenantUuid: string, organizationUuid: string};

  public appliedToInfoVisible: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private tenantsService: TenantsService,
    public jsonEditorService: JsonEditorService,
    private authGuard: AuthGuard,
    private loggedUser: LoggedUser,
    private commonMessagesService: CommonMessagesService,
    private triggerRulesService: TriggerRulesService,
    private i18n: TranslocoService
  ) {
    this.i18n.selectTranslation().subscribe(() => {
      this.types = [
        {label: this.i18n.translate('trigger.operation.lt'), value: 'LT'},
        {label: this.i18n.translate('trigger.operation.lte'), value: 'LTE'},
        {label: this.i18n.translate('trigger.operation.gt'), value: 'GT'},
        {label: this.i18n.translate('trigger.operation.gte'), value: 'GTE'},
        {label: this.i18n.translate('trigger.operation.eq'), value: 'EQ'},
        {label: this.i18n.translate('trigger.operation.byteMask'), value: 'BYTE_MASK'},
        {label: this.i18n.translate('trigger.operation.inactivity'), value: 'INACTIVITY'}
      ];
    });
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path, urlSegment[2].path);
      }
    });
  }

  loadCreationForm(): void {
    this.triggerRule = new TriggerRule();
    this.triggerRule.entityType = 'DEVICE';
    if (!this.authGuard.superAdminRequired()) {
      this.triggerRule.tenant = this.tenants[0];
    }
    this.triggerRule.appliedTo = JSON.parse('{ "measurementTypes": [".*"], "measurementNames": [".*"]}');
    this.createForm(this.triggerRule);
  }

  loadEditionForm(tenantUuid, organization, triggerRuleName): void {
    const organizationUuid = organization === 'global' ? null : organization;

    this.tenantsService.getTenantTriggerRule(tenantUuid, organizationUuid, triggerRuleName).toPromise().then(response => {
      this.triggerRule = response.triggerRule;

      this.tenants = [this.triggerRule.tenant];
      this.organizations = [this.triggerRule.organization];
      this.editInfo = {
        oldName: this.triggerRule.name,
        tenantUuid: this.triggerRule.tenant.uuid,
        organizationUuid: this.triggerRule.organization ? this.triggerRule.organization.uuid : null
      };
      this.triggerRule.appliedTo = JSON.parse(this.triggerRule.appliedTo);

      this.createForm(this.triggerRule);
    });
  }

  private createForm(triggerRule: TriggerRule): void {
    this.triggerRuleForm = this.formBuilder.group({
      name: [triggerRule.name, [Validators.maxLength(255), Validators.required, noSpecialCharacters, noWhitespaces]],
      expression: [triggerRule.expression, [Validators.maxLength(255), Validators.required]],
      type: [triggerRule.type, [Validators.maxLength(255), Validators.required]],
      entityType: [triggerRule.entityType, Validators.required],
      appliedTo: [triggerRule.appliedTo, Validators.maxLength(255)],
      tenant: [triggerRule.tenant, [Validators.required]],
      organization: [{value: triggerRule.organization, disabled: true}],
      category: [this.mode === 'create' ? 'ALARM' : triggerRule.category, Validators.required],
      disabledRepeatedEvents: [this.mode === 'create' ? false : triggerRule.disabledRepeatedEvents, Validators.required],
    });
    this.triggerRuleForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.triggerRuleForm.get('tenant').disable();
      this.loggedUser.userObservable.subscribe(user => {
        if (user.organization) {
          this.tenantsService.getTenantOrganizationSummaries(user.tenant).subscribe(response => {
            this.organizations = response.organizations;
            this.triggerRuleForm.get('organization').setValue(this.organizations[0]);
          });
        } else {
          this.loadOrganizations(user.tenantUuid);
        }
      });
    }

    if (this.mode === 'edit') {
      this.triggerRuleForm.get('tenant').disable();
    }
  }

  private loadOrganizations(tenantUuid: string): void {
    this.triggerRuleForm.get('organization').disable();
    this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise().then(response => {
      this.organizations = response.organizations;
      if ( this.tenants.length === 1 && this.organizations.length === 1 ) {
        this.triggerRuleForm.get('organization').setValue(this.organizations[0]);
      } else {
        this.triggerRuleForm.get('organization').enable();
      }
    });
  }

  tenantChanged(event): void {
    if (event.value) {
      this.triggerRuleForm.get('organization').setValue(null);
      this.loadOrganizations(event.value.uuid);
    }
  }

  persistTriggerRuleData(): void {
    if (!this.triggerRuleForm.valid) { return; }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.triggerRulesService.createTriggerRule(persistData).toPromise();
    } else {
      promise = this.triggerRulesService.editTriggerRule(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/alarms/trigger-rules`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): TriggerRulePersistRequest {
    const formValue = this.triggerRuleForm.getRawValue();
    return {
      name: formValue.name,
      expression: formValue.expression,
      type: formValue.type,
      entityType: formValue.entityType,
      appliedTo: JSON.stringify(formValue.appliedTo),
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      organizationUuid: this.mode === 'edit' ? this.editInfo.organizationUuid : formValue.organization ? formValue.organization.uuid : null,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null,
      category: formValue.category,
      disabledRepeatedEvents: formValue.disabledRepeatedEvents
    };
  }

  onTypeChange(event): void {
    if (event.value === 'BYTE_MASK') {
      this.triggerRuleForm.get('expression').setValue(0);
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.triggerRuleForm.untouched;
  }
}
